import React from 'react';
import { IonContent, IonPage } from '@ionic/react';
import './TermsPage.css';

const TERMS: Record<string, string> = {
  es: `TÉRMINOS Y CONDICIONES DE USO — AnApp

Última actualización: Febrero 2026

1. ACEPTACIÓN
Al usar AnApp aceptas estos Términos en su totalidad. Si no estás de acuerdo, no utilices la plataforma.

2. DESCRIPCIÓN DEL SERVICIO
AnApp es una plataforma digital para la publicación y promoción de eventos. Los usuarios pueden publicar y gestionar eventos mediante el pago de una tarifa establecida.

3. DERECHOS DE PUBLICACIÓN
AnApp se reserva el derecho de publicar, moderar, rechazar o eliminar cualquier contenido, incluso si el usuario ha realizado un pago. La decisión del equipo de moderación es definitiva.

4. CONTENIDO PROHIBIDO
Queda estrictamente prohibido publicar:
- Contenido pornográfico o de naturaleza sexual explícita
- Contenido violento, gore o perturbador
- Material racista, xenófobo o que incite al odio
- Contenido que promueva ideologías extremistas o discriminatorias
- Material religioso ofensivo o que incite a la intolerancia
- Contenido ilegal o que infrinja derechos de terceros
- Publicidad engañosa o fraudulenta

5. POLÍTICA DE REEMBOLSO
Los pagos por publicación de eventos son no reembolsables, excepto cuando:
- AnApp rechace el contenido por causas técnicas imputables a la plataforma.
- El servicio presente fallas graves que impidan la publicación.
El incumplimiento de las políticas de contenido no da derecho a reembolso.

6. MODERACIÓN
Todos los eventos pasan por revisión antes de ser publicados. El proceso puede tardar hasta 48 horas hábiles.

7. PROPIEDAD INTELECTUAL
El usuario garantiza ser titular de los derechos del contenido publicado o contar con las licencias necesarias. AnApp no se responsabiliza por infracciones cometidas por los usuarios.

8. LIMITACIÓN DE RESPONSABILIDAD
AnApp no será responsable por daños derivados del uso de la plataforma, incluyendo el rechazo de publicaciones por incumplimiento de estas políticas.

9. PRIVACIDAD
El uso de AnApp implica la aceptación de nuestra Política de Privacidad.

10. MODIFICACIONES
AnApp puede modificar estos términos en cualquier momento. El uso continuo del servicio implica la aceptación de los cambios.

11. SUSPENSIÓN DE CUENTAS
AnApp puede suspender o cancelar cuentas que incumplan estos términos sin previo aviso y sin derecho a reembolso.

Al usar AnApp confirmas haber leído y aceptado estos Términos y Condiciones.`,

  en: `TERMS AND CONDITIONS — AnApp

Last updated: February 2026

1. ACCEPTANCE
By using AnApp you fully accept these Terms. If you disagree, do not use the platform.

2. SERVICE DESCRIPTION
AnApp is a digital platform for publishing and promoting events. Users may publish and manage events by paying an established fee.

3. PUBLICATION RIGHTS
AnApp reserves the right to publish, moderate, reject or remove any content, even if the user has made a payment. The moderation team's decision is final.

4. PROHIBITED CONTENT
The following content is strictly prohibited:
- Pornographic or sexually explicit material
- Violent, gory or disturbing content
- Racist, xenophobic or hate-inciting material
- Content promoting extremist or discriminatory ideologies
- Offensive religious content or content inciting intolerance
- Illegal content or content infringing third-party rights
- Misleading or fraudulent advertising

5. REFUND POLICY
Payments for event publication are non-refundable, except when:
- AnApp rejects content due to technical issues attributable to the platform.
- The service experiences serious failures preventing publication.
Violations of content policies do not entitle users to a refund.

6. MODERATION
All events are reviewed before publication. The process may take up to 48 business hours.

7. INTELLECTUAL PROPERTY
Users guarantee they hold rights to the content they publish or have the necessary licenses. AnApp is not responsible for infringements committed by users.

8. LIMITATION OF LIABILITY
AnApp shall not be liable for damages arising from use of the platform, including rejection of publications due to policy violations.

9. PRIVACY
Use of AnApp implies acceptance of our Privacy Policy.

10. MODIFICATIONS
AnApp may modify these terms at any time. Continued use of the service implies acceptance of the changes.

11. ACCOUNT SUSPENSION
AnApp may suspend or cancel accounts that violate these terms without prior notice and without right to a refund.

By using AnApp you confirm you have read and accepted these Terms and Conditions.`,

  pt: `TERMOS E CONDIÇÕES DE USO — AnApp

Última atualização: Fevereiro 2026

1. ACEITAÇÃO
Ao usar o AnApp você aceita integralmente estes Termos. Caso não concorde, não utilize a plataforma.

2. DESCRIÇÃO DO SERVIÇO
O AnApp é uma plataforma digital para publicação e promoção de eventos. Os usuários podem publicar e gerenciar eventos mediante o pagamento de uma taxa estabelecida.

3. DIREITOS DE PUBLICAÇÃO
O AnApp reserva-se o direito de publicar, moderar, rejeitar ou remover qualquer conteúdo, mesmo que o usuário tenha efetuado um pagamento. A decisão da equipe de moderação é definitiva.

4. CONTEÚDO PROIBIDO
É estritamente proibido publicar:
- Conteúdo pornográfico ou de natureza sexual explícita
- Conteúdo violento, perturbador ou gore
- Material racista, xenófobo ou que incite ao ódio
- Conteúdo que promova ideologias extremistas ou discriminatórias
- Material religioso ofensivo ou que incite à intolerância
- Conteúdo ilegal ou que infrinja direitos de terceiros
- Publicidade enganosa ou fraudulenta

5. POLÍTICA DE REEMBOLSO
Os pagamentos pela publicação de eventos são não reembolsáveis, exceto quando:
- O AnApp rejeitar o conteúdo por motivos técnicos imputáveis à plataforma.
- O serviço apresentar falhas graves que impeçam a publicação.
O descumprimento das políticas de conteúdo não dá direito a reembolso.

6. MODERAÇÃO
Todos os eventos passam por revisão antes de serem publicados. O processo pode levar até 48 horas úteis.

7. PROPRIEDADE INTELECTUAL
O usuário garante ser titular dos direitos do conteúdo publicado ou possuir as licenças necessárias. O AnApp não se responsabiliza por infrações cometidas pelos usuários.

8. LIMITAÇÃO DE RESPONSABILIDADE
O AnApp não será responsável por danos decorrentes do uso da plataforma, incluindo a rejeição de publicações por descumprimento destas políticas.

9. PRIVACIDADE
O uso do AnApp implica a aceitação de nossa Política de Privacidade.

10. MODIFICAÇÕES
O AnApp pode modificar estes termos a qualquer momento. O uso contínuo do serviço implica a aceitação das alterações.

11. SUSPENSÃO DE CONTAS
O AnApp pode suspender ou cancelar contas que violem estes termos sem aviso prévio e sem direito a reembolso.

Ao usar o AnApp você confirma ter lido e aceito estes Termos e Condições.`,
};

const getLangFromPath = (): string => {
  const path = window.location.pathname;
  if (path.startsWith('/en/')) return 'en';
  if (path.startsWith('/pt-BR/')) return 'pt';
  return 'es';
};

const TermsPage: React.FC = () => {
  const language = getLangFromPath();
  const content = TERMS[language] ?? TERMS.es;
  const homeUrl = language === 'en' ? '/en/' : language === 'pt' ? '/pt-BR/' : '/es/';
  const backLabel = language === 'en' ? '← Home' : language === 'pt' ? '← Início' : '← Inicio';
  const title = language === 'en' ? 'Terms & Conditions' : language === 'pt' ? 'Termos e Condições' : 'Términos y Condiciones';

  return (
    <IonPage>
      <IonContent className="terms-content">
        <div className="terms-container">
          <a href={homeUrl} className="terms-back-link">{backLabel}</a>
          <div className="terms-body">
            <h1 className="terms-title">{title}</h1>
            <pre className="terms-text">{content}</pre>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default TermsPage;
